import React, { useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { FieldGroup, FormBuilder } from "react-reactive-form";
import StandardPDQuestions from "../../questions/StandardPDQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import RedirectHandler from "../../components/RedirectHandler";
import { useParams } from "react-router-dom";
import { populateValueHelperArray } from "./utils/saveRestoreUtils";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";

const isProduction = process.env.NODE_ENV === "production";

const StandardPharmacodynamics = () => {
  const { studyId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacySensitivityNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacySensitivityNames: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
    qPkStdColumn: "object",
    qPkTimelines: "object",
    qPkAnalytes: "object",
  };

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [showRedirect, setShowRedirect] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, setRestoreVersion] = useState(0);
  const lastRestoredQuestionsDataRef = useRef(null);

  useEffect(() => {
    if (!questionsDataSaved) {
      setAjaxLoading(true);
      CsrAPI.getQuestions({ studyId })
        .then(questionsDataSaved => {
          replaceNullArrays(questionsDataSaved);
          setQuestionsDataSaved(questionsDataSaved);
          setErrMsg("");
        })
        .catch(error => {
          if (isProduction) {
            Sentry.captureException(error);
          }
          if (!error?.response) {
            setErrMsg("No Server Response");
          } else if (error.response?.status === 401) {
            setErrMsg("Invalid user credentials");
          } else if (error.response?.status === 403) {
            setShowRedirect(true);
          } else {
            setErrMsg("Failed to get questions");
          }
          errRef.current?.focus();
        })
        .finally(() => {
          setAjaxLoading(false);
        });
    }
  }, [studyId, setQuestionsDataSaved, questionsDataSaved]);

  const replaceNullArrays = obj => {
    for (let key in obj) {
      if (typeof obj[key] === "object") {
        if (Array.isArray(obj[key])) {
          if (obj[key].every(value => value === null)) {
            obj[key] = [];
          }
        } else {
          replaceNullArrays(obj[key]);
        }
      }
    }
  };

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      if (lastRestoredQuestionsDataRef.current === questionsDataSaved) {
        return;
      }
      lastRestoredQuestionsDataRef.current = questionsDataSaved;

      const wizardFormValue = questionsDataSaved.wizardForm;

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (formArraysKeys[key] && wizardForm.controls[key]) {
          while (wizardForm.controls[key].length > 0) {
            wizardForm.controls[key].removeAt(0);
          }
        }
      });

      Object.keys(wizardFormValue).map(function (key) {
        if (formArraysKeys[key]) {
          if (formArraysKeys[key] === "object") {
            wizardFormValue[key].forEach(item => {
              wizardForm.controls[key]?.push(FormBuilder.group(item));
            });
          } else {
            wizardFormValue[key].forEach(item => {
              wizardForm.controls[key].push(FormBuilder.control(item));
            });
          }
        } else {
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });

      // Extract qPdStdEnabled from root (new format) or inside qPdStdAnalyses (legacy)
      const stdPdEnabled =
        wizardFormValue.qPdStdEnabled ??
        wizardFormValue.qPdStdAnalyses?.qPdStdEnabled ??
        null;
      if (!wizardForm.contains("qPdStdEnabled")) {
        wizardForm.addControl(
          "qPdStdEnabled",
          FormBuilder.control(stdPdEnabled)
        );
      } else {
        wizardForm.patchValue({ qPdStdEnabled: stdPdEnabled });
      }

      // Ensure qComparative control exists
      if (!wizardForm.contains("qComparative")) {
        wizardForm.addControl(
          "qComparative",
          FormBuilder.control(wizardFormValue.qComparative || "false")
        );
      }

      // Restore Standard PD blocks from qPdStdAnalyses (flat array or legacy object) or qPdAnalyses (legacy)
      const stdAnalyses = wizardFormValue.qPdStdAnalyses;
      const stdPdBlocks = Array.isArray(stdAnalyses)
        ? stdAnalyses
        : (stdAnalyses?.blocks ??
          (Array.isArray(wizardFormValue.qPdAnalyses)
            ? wizardFormValue.qPdAnalyses
            : []));
      if (stdPdBlocks.length > 0) {
        stdPdBlocks.forEach((block, index) => {
          const suffix = index > 0 ? `_${index + 1}` : "";

          if (block.qPdTable !== undefined && block.qPdTable !== null) {
            const controlName = `qPdTable${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdTable);
          }

          if (
            block.qPdPopulation !== undefined &&
            block.qPdPopulation !== null
          ) {
            const controlName = `qPdPopulation${suffix}`;
            if (!wizardForm.contains(controlName)) {
              wizardForm.addControl(controlName, FormBuilder.control(""));
            }
            wizardForm.get(controlName).setValue(block.qPdPopulation);
          }

          // Restore qPdColumnHeaders array (Standard PD specific)
          const qPdColumnHeadersControlName = `qPdColumnHeaders${suffix}`;
          populateValueHelperArray(
            wizardForm,
            qPdColumnHeadersControlName,
            block.qPdColumnHeaders
          );

          // Restore qPdTimepoints array (Standard PD specific)
          populateValueHelperArray(
            wizardForm,
            `qPdTimepoints${suffix}`,
            block.qPdTimepoints
          );
        });
      }

      setRestoreVersion(v => v + 1);
    }
  };

  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setShowRedirect,
    setIsSaving,
    questionsRef: null,
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {questionsData.current && (
              <FieldGroup
                control={wizardForm}
                render={() => (
                  <form name={"wizardForm"}>
                    <StandardPDQuestions
                      globalId={0}
                      wizardForm={wizardForm}
                      questionsData={questionsData.current}
                    />
                  </form>
                )}
              />
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Answer the following questions related to Standard Pharmacodynamics"
      }
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Bioavailability",
        buttonGoToAction: "/csr/" + studyId + "/bioavailability",
      }}
      nextButton={{
        buttonTitle: "Comparative PD",
        buttonGoToAction: "/csr/" + studyId + "/comppharmacodynamics",
      }}
      saveButtonHandler={handleSubmit}
      saveButton={true}
    />
  );
};

export default StandardPharmacodynamics;
